// Builders for the WorkflowDebugPanel "collected data" — shared by the application-flow
// screens (Payment / Acceptance / Confirmation) so the debug view is identical everywhere.

import {
  computeAvpNetPremium,
  computeWoonhuisNetPremium,
  computePersonenautoNetPremium,
  computeRechtsbijstandNetPremium,
  composePremiumBreakdown,
} from '@sluitstraat/core';
import { computeInboedelNetFromSections } from './inboedelPricing';
import { computeSubscriptionFee } from './subscriptionFee';
import { computePolicyCost } from './policyCost';
import { getProductOrder } from '../workflowConfig';

// Premium-related field tokens in the SAA contract document (any level): totals (TTOT/BTP/NTP),
// tax (TASS), provision (TTPPRV), discounts (TKRT / package PRCPKKT), policy cost (PKST), etc.
const PREMIUM_KEY = /_(TTOT|BTP|NTP|TASS|TTPPRV|TKRT|TPKRTVM|PKRTVMP|PRCPKKT|PKST|TPP|TTSL|VERZSOM|WPREMBP)$/;

// Walk a product/package object and collect every premium-related leaf field with its path,
// so provision/discount/tax/"all sorts of stuff" surface without hardcoding per-product paths.
export function extractPremiumFields(obj: unknown, prefix = ''): Record<string, number | string> {
  const out: Record<string, number | string> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object') {
      Object.assign(out, extractPremiumFields(value, path));
    } else if (PREMIUM_KEY.test(key) && (typeof value === 'number' || typeof value === 'string')) {
      out[path] = value;
    }
  }
  return out;
}

// Session IDs shown in every collected-data panel.
export function readDebugIds(): Record<string, string | null> {
  return {
    relationId: sessionStorage.getItem('vp_idnr'),
    quoteId: sessionStorage.getItem('quote_id'),
    calculationId: sessionStorage.getItem('calculation_id'),
  };
}

// The createRequest request/response, persisted on the Confirmation screen in local dev only
// (see ConfirmationScreen) so the EndPage can surface them. Returns undefined values otherwise.
export function readCreateRequestDebug(): { request: unknown; response: unknown } {
  const parse = (key: string) => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  };
  return { request: parse('debug_create_request'), response: parse('debug_create_response') };
}

// Package-level enriched calculation document (summary-endpoint aggregate), parsed safely.
export function readEnrichedDocument(): unknown {
  try {
    const raw = sessionStorage.getItem('enriched_calculation_document');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Per-selected-product workflow data, keyed by product.
export function collectProducts(selectedProducts: any, data: any): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const product of getProductOrder()) {
    if (selectedProducts?.[product]) out[product] = data?.[product] ?? null;
  }
  return out;
}

// Computed premium summary (the same numbers the PremiumBreakdownCard shows) + the raw
// premium fields per product. Wrapped in try/catch per product so a malformed product never
// breaks the whole debug panel.
export function buildPremiumDebugSection(selectedProducts: any, data: any, config: any): Record<string, unknown> {
  const nets: { netPremium: number; netPremiumPrecise: number }[] = [];
  const perProduct: Record<string, unknown> = {};

  const add = (key: string, net: { netPremium: number; netPremiumPrecise: number } | null) => {
    if (net && net.netPremium > 0) {
      nets.push(net);
      perProduct[key] = { netPremium: net.netPremium, netPremiumPrecise: net.netPremiumPrecise };
    }
  };

  try { if (selectedProducts?.personenauto) add('personenauto', computePersonenautoNetPremium(data?.['personenauto'], config)); } catch { /* skip */ }
  try { if (selectedProducts?.woonhuis) add('woonhuis', computeWoonhuisNetPremium(data?.['woonhuis'], config)); } catch { /* skip */ }
  try { if (selectedProducts?.inboedel) add('inboedel', computeInboedelNetFromSections(data?.['inboedel'], config)); } catch { /* skip */ }
  try { if (selectedProducts?.aansprakelijkheid) add('aansprakelijkheid', computeAvpNetPremium(data?.['aansprakelijkheid'], config)); } catch { /* skip */ }
  try {
    if (selectedProducts?.rechtsbijstand) {
      const rbData = data?.['rechtsbijstand'];
      const rbDekkingen = rbData?.Supportsection?.Dekkingen || {};
      add('rechtsbijstand', computeRechtsbijstandNetPremium(rbData, config, rbDekkingen));
    }
  } catch { /* skip */ }

  const prolongationFeeConfig = config?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.5) : 0;

  let breakdown: any = null;
  try { breakdown = composePremiumBreakdown(nets, prolongationFee); } catch { /* skip */ }

  let subscription: any = null;
  try { subscription = computeSubscriptionFee(selectedProducts, config); } catch { /* skip */ }

  let policyCost: any = null;
  try { policyCost = computePolicyCost(selectedProducts, config); } catch { /* skip */ }

  // Raw premium fields per selected product (TTOT/BTP/NTP/TASS/TTPPRV/TKRT/PRCPKKT…)
  const rawPremiumFields: Record<string, unknown> = {};
  for (const product of getProductOrder()) {
    if (selectedProducts?.[product]) rawPremiumFields[product] = extractPremiumFields(data?.[product]);
  }

  return {
    perProductNet: perProduct,
    breakdown: breakdown && {
      netTotal: breakdown.total,
      tax: breakdown.tax,
      collectionFee: breakdown.collectionFee,
    },
    prolongationFee,
    subscription,
    policyCost,
    rawPremiumFields,
  };
}
